import { useEffect, useRef, useState, createContext } from "react";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onIdTokenChanged,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import {
  getFirestore,
  Firestore,
  getDoc,
  setDoc,
  doc,
} from "firebase/firestore";
import nookies from "nookies";
import { useRouter } from "next/router";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const router = useRouter();
  const providedToken = router.query.idToken as string | undefined;
  const recoveredToken = useRef<string>();
  const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
  const auth = getAuth(app);
  const db = getFirestore(app);

  useEffect(() => {
    let active = true;
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setAuthLoading(true);
      try {
        // Keep the app handoff identity until the user explicitly signs in.
        const handoffToken =
          providedToken !== recoveredToken.current ? providedToken : undefined;
        const token = handoffToken || (user ? await user.getIdToken() : "");
        if (!active || user !== auth.currentUser) return;
        nookies.set(undefined, "token", token, { path: "/" });
        setUser(user);
      } catch (error) {
        if (active && user === auth.currentUser) {
          setAuthError(resolveErrorMessage(error.message));
        }
      } finally {
        if (active && user === auth.currentUser) setAuthLoading(false);
      }
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [auth, providedToken]);

  const signUpWithEmailAndPassword = async (
    email: string,
    password: string
  ) => {
    try {
      setAuthLoading(true);
      const { user } = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        setDoc(doc(db, "users", user.uid), {});
      }
    } catch (error) {
      console.error(error);
      setAuthError(resolveErrorMessage(error.message));
    } finally {
      setAuthLoading(false);
    }
  };

  const signInUser = async (
    email: string,
    password: string
  ): Promise<boolean> => {
    try {
      setAuthLoading(true);
      setAuthError("");
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      // An auth listener must not put this rejected handoff token back into
      // the cookie after a successful manual sign-in.
      recoveredToken.current = providedToken;
      const token = await user.getIdToken();
      if (user !== auth.currentUser) return false;
      nookies.set(undefined, "token", token, { path: "/" });
      setUser(user);
      return true;
    } catch (error) {
      setAuthError(resolveErrorMessage(error.message));
      return false;
    } finally {
      setAuthLoading(false);
    }
  };

  const signOutUser = async () => {
    await signOut(auth);
  };

  const value = {
    signOutUser,
    signInUser,
    signUpWithEmailAndPassword,
    user,
    db,
    authLoading,
    authError,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
export const AuthContext = createContext<AuthContext | null>(null);

export default AuthContextProvider;

export type UserData = {
  email: string;
  uid: string;
  accessToken?: string;
  displayName?: string;
};

export type SubscriptionData = {
  subscriptionId: string;
  subscriptionPeriodStart: number;
  subscriptionPeriodEnd: number;
  subscriptionDaysUntilDue: number | null;
  subscriptionStatus:
    | "incomplete"
    | "incomplete_expired"
    | "trialing"
    | "active"
    | "past_due"
    | "canceled"
    | "unpaid";
};

type AuthContext = {
  user: UserData;
  signInUser: (email: string, password: string) => Promise<boolean>;
  signOutUser: () => void;
  signUpWithEmailAndPassword: (email: string, password: string) => void;
  db: Firestore;
  authLoading: boolean;
  authError: string;
  setUser: (user: UserData) => void;
};

const resolveErrorMessage = (error: string) => {
  switch (error) {
    case "Firebase: Error (auth/user-not-found).":
      return "User not found";
    case "Firebase: Error (auth/wrong-password).":
      return "Wrong password";
    case "Firebase: Error (auth/email-already-in-use).":
      return "Email already in use";
    case "Firebase: Error (auth/weak-password).":
      return "Password is too weak";
    default:
      return "Something went wrong";
  }
};
